import { IBundleContext } from "../Contexts/BundleContext";
import { BundleModel } from "../Models/BundleModel";
import { BundleThemeModel } from "../Models/BundleThemeModel";
import { IComponent } from "../Models/IComponent";
import { IContainerComponent } from "../Models/IContainerComponent";
import { SharedComponentModel } from "../Models/SharedComponentModel";
import { ViewModel } from "../Models/ViewModel";
import { SHARED_COMPONENT_ROOT_TYPE } from "../Types/DesignerMessages";
import { updateComponentFromRoot } from "./ComponentTreeUtils";

export type BundleComponentUpdateScope = "View" | "SharedComponent";

export type BundleComponentUpdate = {
  scope: BundleComponentUpdateScope;
  ownerId: string;
  componentPath: string[];
  component: IComponent;
};

export type BundleMetadataUpdate = {
  theme?: Record<string, any>;
  themes?: BundleThemeModel[];
  settings?: IBundleContext["Settings"];
  embeddedResources?: BundleModel["EmbeddedResources"];
  enums?: BundleModel["Enums"];
};

function indexById<T extends { Id: string }>(items: T[] | undefined): Record<string, T> {
  return (items || []).reduce(
    (acc, item) => {
      acc[item.Id] = item;
      return acc;
    },
    {} as Record<string, T>
  );
}

export function applyBundleMetadataUpdateToBundleContext(current: IBundleContext | null, update: BundleMetadataUpdate): IBundleContext | null {
  if (!current) {
    return current;
  }

  const selectedTheme =
    update.themes?.find((theme) => theme.Id === current.SelectedThemeId) ??
    update.themes?.find((theme) => theme.IsDefault);

  return {
    ...current,
    Theme: selectedTheme?.Theme ?? update.theme ?? current.Theme,
    Themes: update.themes ?? current.Themes,
    SelectedThemeId: selectedTheme?.Id ?? current.SelectedThemeId,
    Settings: update.settings ?? current.Settings,
    EnumById: update.enums ? indexById(update.enums) : current.EnumById,
    EmbeddedResourceById: update.embeddedResources ? indexById(update.embeddedResources) : current.EmbeddedResourceById,
  };
}

export function applyBundleMetadataUpdateToBundleModel(current: BundleModel | null, update: BundleMetadataUpdate): BundleModel | null {
  if (!current) {
    return current;
  }

  return {
    ...current,
    Theme: update.themes?.find((theme) => theme.IsDefault)?.Theme ?? update.theme ?? current.Theme,
    Themes: update.themes ?? current.Themes,
    Settings: update.settings ?? current.Settings,
    Enums: update.enums ?? current.Enums,
    EmbeddedResources: update.embeddedResources ?? current.EmbeddedResources,
  };
}

export function applyBundleComponentUpdateToBundleContext(current: IBundleContext | null, update: BundleComponentUpdate): IBundleContext | null {
  if (!current) {
    return current;
  }

  if (update.scope === "View") {
    const currentView = current.ViewById[update.ownerId];
    if (!currentView) {
      return current;
    }

    const updatedView =
      update.componentPath.length === 0
        ? (update.component as ViewModel)
        : (updateComponentFromRoot(currentView, update.componentPath, update.component) as ViewModel);

    return {
      ...current,
      ViewById: {
        ...current.ViewById,
        [update.ownerId]: updatedView,
      },
    };
  }

  const currentSharedComponent = current.SharedComponentById[update.ownerId];
  if (!currentSharedComponent) {
    return current;
  }

  const updatedSharedComponent: SharedComponentModel = {
    ...currentSharedComponent,
    Component: updateComponentFromRoot(currentSharedComponent.Component as IContainerComponent, update.componentPath, update.component),
  };

  return {
    ...current,
    SharedComponentById: {
      ...current.SharedComponentById,
      [update.ownerId]: updatedSharedComponent,
    },
  };
}

export function applyBundleComponentUpdateToBundleModel(current: BundleModel | null, update: BundleComponentUpdate): BundleModel | null {
  if (!current) {
    return current;
  }

  if (update.scope === "View") {
    return {
      ...current,
      Views: current.Views.map((view) =>
        view.Id === update.ownerId
          ? update.componentPath.length === 0
            ? (update.component as ViewModel)
            : updateComponentFromRoot(view, update.componentPath, update.component)
          : view
      ),
    };
  }

  return {
    ...current,
    SharedComponents: current.SharedComponents.map((sharedComponent) =>
      sharedComponent.Id === update.ownerId
        ? {
            ...sharedComponent,
            Component: updateComponentFromRoot(sharedComponent.Component as IContainerComponent, update.componentPath, update.component),
          }
        : sharedComponent
    ),
  };
}

export function applyBundleComponentUpdateToDesignerModel(current: IComponent | null, update: BundleComponentUpdate): IComponent | null {
  if (!current) {
    return current;
  }

  if (update.scope === "View" && current.Id === update.ownerId) {
    return update.componentPath.length === 0
      ? update.component
      : updateComponentFromRoot(current as IContainerComponent, update.componentPath, update.component);
  }

  if (update.scope === "SharedComponent" && (current as any).Type === SHARED_COMPONENT_ROOT_TYPE && current.Id === update.ownerId) {
    const currentChildren = (current as IContainerComponent).Children ?? [];
    const currentRoot = currentChildren[0] as IContainerComponent | undefined;
    if (!currentRoot) {
      return current;
    }

    return {
      ...current,
      Children: [updateComponentFromRoot(currentRoot, update.componentPath, update.component)],
    } as IComponent;
  }

  return current;
}
